import { createBrowserRouter, Navigate } from 'react-router-dom';
import { isDefinitelyOnline } from '@/shared/onlineState';
import { RequireAuth } from '@/modules/auth/presentation/guards/RequireAuth';
import { RequireProfile } from '@/modules/profiles/presentation/guards/RequireProfile';
import { RequireAdmin } from '@/modules/admin/presentation/guards/RequireAdmin';
import { RootLayout } from '@/presentation/layouts/RootLayout';
import { KioskShell } from '@/shared/ui/layouts/KioskShell';

// Synchronous imports — all code bundled in main chunk for offline-first PWA
import { LoginPage } from '@/modules/auth/presentation/pages/LoginPage';
import { ProfileGate } from '@/modules/profiles/presentation/components/ProfileGate';
import { HomePage } from '@/modules/terminal/presentation/pages/HomePage';
import { ScanningPage } from '@/modules/attendance/presentation/pages/ScanningPage';
import { CardScanPage } from '@/modules/attendance/presentation/pages/CardScanPage';
import { IdInputPage } from '@/modules/attendance/presentation/pages/IdInputPage';
import { PinPage } from '@/modules/attendance/presentation/pages/PinPage';
import { MarkingSuccessPage } from '@/modules/attendance/presentation/pages/MarkingSuccessPage';
import { MarkingErrorPage } from '@/modules/attendance/presentation/pages/MarkingErrorPage';
import { EmployeePortalPage } from '@/modules/employees/presentation/pages/EmployeePortalPage';
import { AdminDashboardPage } from '@/modules/admin/presentation/pages/AdminDashboardPage';
import { AdminEmployeesPage } from '@/modules/admin/presentation/pages/AdminEmployeesPage';
import { AdminEmployeeDetailPage } from '@/modules/admin/presentation/pages/AdminEmployeeDetailPage';
import { AdminMarcajesPage } from '@/modules/admin/presentation/pages/AdminMarcajesPage';
import { AdminConfigPage } from '@/modules/admin/presentation/pages/AdminConfigPage';
import { EnrollPinPage } from '@/modules/admin/presentation/pages/EnrollPinPage';
import { AdminEnrollFacePage } from '@/modules/admin/presentation/pages/AdminEnrollFacePage';
import { EnrollDuplicateFaceErrorPage } from '@/modules/admin/presentation/pages/EnrollDuplicateFaceErrorPage';
import { AdminEnrollCardPage } from '@/modules/admin/presentation/pages/AdminEnrollCardPage';
import { AdminEnrollCardDuplicateErrorPage } from '@/modules/admin/presentation/pages/AdminEnrollCardDuplicateErrorPage';
import { FaceAuthErrorPage } from '@/modules/attendance/presentation/pages/FaceAuthErrorPage';
import { ErrorSentryPage } from '@/shared/ui/error/error.sentry';

/** Redirects unmatched routes: login when online, home when offline. */
function OfflineSafeRedirect() {
  const to = isDefinitelyOnline() ? '/login' : '/';
  return <Navigate to={to} replace />;
}

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: <ErrorSentryPage />,
    children: [
      // Public
      { path: '/login', element: <LoginPage /> },

      // Requires valid auth token
      {
        element: <RequireAuth />,
        children: [
          { path: '/select-profile', element: <ProfileGate /> },

          // Requires a profile to be selected
          {
            element: <RequireProfile />,
            children: [
              {
                element: <KioskShell />,
                children: [
                  { path: '/',                   element: <HomePage /> },
                  { path: '/scan/:action',       element: <ScanningPage /> },
                  { path: '/card-scan/:action',  element: <CardScanPage /> },
                  { path: '/id-input/:action',   element: <IdInputPage /> },
                  { path: '/admin-pin/:action',  element: <PinPage /> },
                  { path: '/pin/:action',        element: <PinPage /> },
                  { path: '/success',            element: <MarkingSuccessPage /> },
                  { path: '/error',              element: <MarkingErrorPage /> },
                  { path: '/face-error',         element: <FaceAuthErrorPage /> },
                  { path: '/empleado/:idPerson', element: <EmployeePortalPage /> },

                  // Requires active admin session (30-min TTL)
                  {
                    element: <RequireAdmin />,
                    children: [
                      { path: '/admin',                                element: <AdminDashboardPage /> },
                      { path: '/admin/empleados',                      element: <AdminEmployeesPage /> },
                      { path: '/admin/empleados/:idEntity',            element: <AdminEmployeeDetailPage /> },
                      { path: '/admin/empleados/:idPerson/enroll/photo', element: <AdminEnrollFacePage /> },
                      { path: '/admin/empleados/:idPerson/enroll/photo/duplicate-face', element: <EnrollDuplicateFaceErrorPage /> },
                      { path: '/admin/empleados/:idPerson/enroll/card', element: <AdminEnrollCardPage /> },
                      { path: '/admin/empleados/:idPerson/enroll/card/duplicate', element: <AdminEnrollCardDuplicateErrorPage /> },
                      { path: '/admin/empleados/:idEntity/enroll/pin',  element: <EnrollPinPage /> },
                      { path: '/admin/marcajes',                       element: <AdminMarcajesPage /> },
                      { path: '/admin/configuracion',                  element: <AdminConfigPage /> },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },

      // Unmatched routes → login when online, home when offline (avoids dead-end login screen)
      { path: '*', element: <OfflineSafeRedirect /> },
    ],
  },
]);
